/**
 * ч.1442: підпис документа за посиланням і збереження його у свій журнал ЕДО.
 *
 * Сторінка підпису публічна: запити — окремим клієнтом без перехоплювачів застосунку (ті на 401
 * ведуть на вхід, а людина без облікового запису має лишитись на сторінці). Запити від імені
 * користувача (зберегти в журнал) — лише коли сесія є, з тими самими заголовками, що й застосунок.
 */
import axios from 'axios';
import { getActiveTenantId, isLoggedIn, readCsrfToken } from '@/auth/store';

export interface DiiaWidget { sign_url: string; loader: string; test_env: boolean }
export interface SignWidgets {
  /** Дія.Підпис — віджет id.gov.ua (тестове чи робоче середовище); null — не налаштовано. */
  diia: DiiaWidget | null;
  /** Хмарний ключ / токен — віджет ІІТ. */
  iit: { sign_url: string | null; loader: string };
}

export interface PublicSignInfo {
  filename: string;
  mime_type: string;
  org_name: string;
  subject: string | null;
  status: string;
  expected_signer_code: string | null;
  widgets: SignWidgets;
  /** До якої дати діє посилання (YYYY-MM-DD). */
  expires_at: string;
  sender_signed: boolean;
  sender_signer: string | null;
}

export interface ClaimCandidate { id: string; name: string; code: string; confirmed: boolean }
export interface ClaimInfo {
  subject: string;
  sender: string;
  status: string;
  codes: string[];
  candidates: ClaimCandidate[];
  saved: { organization_id: string; entry_id: string }[];
}

const pub = axios.create({ baseURL: '/api/v1' });

function sessionConfig(write = false) {
  const tenant = getActiveTenantId();
  const csrf = write ? readCsrfToken() : null;
  return {
    withCredentials: true,
    headers: {
      'X-Session': 'cookie',
      ...(tenant ? { 'X-Tenant-Id': tenant } : {}),
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
  };
}

/** Користувач увійшов і обрав акаунт — можна пропонувати зберегти документ. */
export const hasSession = (): boolean => isLoggedIn() && !!getActiveTenantId();

/** Посилання, з якого людина пішла реєструватись, — журнал ЕДО запропонує зберегти документ. */
export const PENDING_CLAIM_KEY = 'edo-pending-claim';

export const edoSign = {
  info: (token: string) =>
    pub.get<PublicSignInfo>(`/edo-public/sign/${token}`).then((r) => r.data),
  file: (token: string) =>
    pub.get<ArrayBuffer>(`/edo-public/sign/${token}/file`, { responseType: 'arraybuffer' })
      .then((r) => new Uint8Array(r.data)),
  fileBlob: (token: string) =>
    pub.get<Blob>(`/edo-public/sign/${token}/file`, { responseType: 'blob' }).then((r) => r.data),
  submit: (token: string, body: { signature_b64: string; signer_name?: string; signer_edrpou?: string }) =>
    pub.post(`/edo-public/sign/${token}/submit`, body).then((r) => r.data),
  widgets: () => pub.get<SignWidgets>('/edo-public/sign-widgets').then((r) => r.data),
  claimInfo: (token: string) =>
    pub.get<ClaimInfo>(`/edo/claim/${token}`, sessionConfig()).then((r) => r.data),
  claim: (token: string, organizationId: string) =>
    pub.post<{ entry_id: string; created: boolean }>(
      `/edo/claim/${token}`, null,
      { ...sessionConfig(true), params: { organization_id: organizationId } }).then((r) => r.data),
};

/** Текст помилки сервера (detail) або запасний. */
export function detailOf(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
}
